import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import type { RootState } from "~/app/store";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { buildProductDescriptor } from "~/domains/pna/descriptor";
import type { PnaSnapshot } from "~/domains/pna/pnaSnapshot";
import { useRegularPnaProductDescriptors } from "~/domains/pna/useRegularPnaProductDescriptors";
import type { LineItem } from "~/features/cart/cart/models/CartData";

/** Descriptor map key: one entry per distinct (productCode, priceReference) pair in the quote. */
export const quoteDescriptorKey = (productCode: string, priceReference: string) => `${productCode}::${priceReference}`;

/**
 * Legacy-equivalent base-less descriptor for a pair the queries never subscribed to. Groups always
 * derive from the same line items as the subscriptions, so this is a defensive fallback only.
 */
export function projectDisabledDescriptor(base: ProductData, priceReference: string): ProductDescriptor {
  const disabledSnapshot: PnaSnapshot = { price: { status: "disabled" }, availability: { status: "disabled" } };
  return buildProductDescriptor(base, disabledSnapshot, priceReference);
}

/**
 * Per-line-item-pair quote descriptors from the canonical PnA queries (A05 active-quote slice).
 * Replaces the legacy `selectProductDescriptor` dictionary reads in the quote view model; bases
 * come from `products.basic`, prices are keyed per (productCode, priceReference) pair.
 */
export function useQuoteLineItemDescriptors(lineItems: LineItem[]): {
  byKey: Map<string, ProductDescriptor>;
  isPnaLoading: boolean;
} {
  const pairs = useMemo(() => {
    const seen = new Set<string>();
    return lineItems.flatMap((item) => {
      const key = quoteDescriptorKey(item.productCode, item.priceReference);
      if (seen.has(key)) {
        return [];
      }
      seen.add(key);
      return [{ productCode: item.productCode, priceReference: item.priceReference }];
    });
  }, [lineItems]);

  const bases = useSelector(
    (state: RootState) => pairs.map((pair) => state.products.basic[pair.productCode]),
    shallowEqual,
  );

  const descriptors = useRegularPnaProductDescriptors(
    pairs.map((pair, index) => ({
      storefrontCode: pair.productCode,
      priceReference: pair.priceReference,
      base: bases[index],
    })),
  );

  const byKey = new Map<string, ProductDescriptor>();
  pairs.forEach((pair, index) =>
    byKey.set(quoteDescriptorKey(pair.productCode, pair.priceReference), descriptors[index]),
  );

  return { byKey, isPnaLoading: descriptors.some((descriptor) => descriptor.isLoading) };
}
